// TUT-450 — os processos: lista, detalhe, atividade, proveniência, criação e edição, Datajud, dispensa de quesitos, assuntos e previsão de etapa.
import { demoApi } from "../demo-data";
import type {
  AgenteDoKpi,
  VinculosDoProcesso,
  TribunalDoProcesso,
  DatajudLookupResult,
  CreateProcessInput,
  StageWaiverStatus,
  ProcessActivityResponse,
  ProcessDetail,
  ProcessListResponse,
  ProcessListQuery,
  ProcessProvenance,
  ProcessSubject,
  ProcessSubjectRenameResponse,
  ProcessSubjectListResponse,
  ProcessSubjectRef,
  SetProcessSubjectsInput,
  CreateProcessSubjectInput,
  ProcessSummary,
  UpdateProcessInput,
} from "../types";
import { ApiError } from "../api-error";
import { comFatia, request } from "./infra";
import type { FatiaPedida } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

// O mesmo formato que o `ParseUUIDPipe({ version: "4" })` da API aceita.
const UUID_V4 =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;

export const apiDosProcessos = {
  processes(query: ProcessListQuery = {}): Promise<ProcessListResponse> {
    if (useDemoApi) return demoApi.processes(query);
    const params = new URLSearchParams({
      page: String(query.page ?? 1),
      pageSize: String(query.pageSize ?? 20),
    });
    if (query.search) params.set("search", query.search);
    if (query.status) params.set("status", query.status);
    for (const subjectId of query.subjectIds ?? []) {
      params.append("subjectId", subjectId);
    }
    if (query.claimant) params.set("claimant", query.claimant);
    if (query.clientOrganizationId)
      params.set("clientOrganizationId", query.clientOrganizationId);
    // TUT-248 fase 2 — VARA, RECLAMADA e a faixa de VALOR DA CAUSA. Comparação
    // com `undefined` nos dois de valor: `0` é ponta legítima da faixa, e a
    // truthiness a descartaria em silêncio.
    if (query.court) params.set("court", query.court);
    if (query.defendant) params.set("defendant", query.defendant);
    if (query.claimAmountMinCents !== undefined)
      params.set("claimAmountMinCents", String(query.claimAmountMinCents));
    if (query.claimAmountMaxCents !== undefined)
      params.set("claimAmountMaxCents", String(query.claimAmountMaxCents));
    if (query.from) params.set("from", query.from);
    if (query.to) params.set("to", query.to);
    // TUT-476 — só os abertos e o período de entrada.
    if (query.abertos) params.set("abertos", "true");
    if (query.periodoDe) params.set("periodoDe", query.periodoDe);
    if (query.periodoAte) params.set("periodoAte", query.periodoAte);
    // TUT-204a: quem ordena é o servidor, sobre o conjunto inteiro.
    if (query.sort) params.set("sort", query.sort);
    if (query.dir) params.set("dir", query.dir);
    return request<ProcessListResponse>(`/processes?${params}`);
  },
  // Laudo pericial (TUT-038). As três escritas são rotas distintas porque são
  // comandos distintos — abrir, transitar e reabrir têm regras diferentes no
  // servidor, e um "salvar laudo" só apagaria essa diferença já na tela.
  // TUT-494 — o que o tribunal publicou do processo (a Linha e o Resumo).
  tribunalDoProcesso(processId: string): Promise<TribunalDoProcesso> {
    if (useDemoApi) return demoApi.tribunalDoProcesso(processId);
    return request<TribunalDoProcesso>(`/processes/${encodeURIComponent(processId)}/tribunal`);
  },
  // TUT-487a (revisão 1) — as medianas da Linha antiga (`processLinePredictions`)
  // saíram daqui: a Linha dos marcos não as usa. A rota da API continua, para
  // quando o dono quiser a "Estimativa" de volta.
  // Catálogo de assuntos (TUT-082). `includeInactive` só é usado pela tela de
  // gestão: o seletor do processo pede o catálogo já filtrado, senão ofereceria
  // para vincular exatamente o assunto que o escritório desligou.
  processSubjects(
    search = "",
    includeInactive = false,
    fatia: FatiaPedida = {},
  ): Promise<ProcessSubjectListResponse> {
    if (useDemoApi)
      return demoApi.processSubjects(search, includeInactive, fatia);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (includeInactive) params.set("includeInactive", "true");
    return request<ProcessSubjectListResponse>(
      `/process-subjects${comFatia(params, fatia)}`,
    );
  },
  // Substitui o conjunto inteiro de assuntos do processo (TUT-082). É PUT
  // porque é substituição: mandar metade e esperar merge é o que produziria
  // dois assuntos principais.
  setProcessSubjects(
    processId: string,
    input: SetProcessSubjectsInput,
  ): Promise<{ items: ProcessSubjectRef[] }> {
    if (useDemoApi) return demoApi.setProcessSubjects(processId, input);
    return request<{ items: ProcessSubjectRef[] }>(
      `/processes/${encodeURIComponent(processId)}/subjects`,
      { method: "PUT", body: JSON.stringify(input) },
    );
  },
  createProcessSubject(
    input: CreateProcessSubjectInput,
  ): Promise<ProcessSubject> {
    if (useDemoApi) return demoApi.createProcessSubject(input);
    return request<ProcessSubject>("/process-subjects", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  /**
   * TUT-172 — a rota da ATIVAÇÃO saiu do `PATCH` e virou `POST :id/active`.
   *
   * O `PATCH` da ficha passou a ser a correção do rótulo, alinhando os assuntos
   * com os outros cinco cadastros. Renomear reescreve o passado e desativar
   * não; enfiar os dois na mesma chamada faria deles o mesmo gesto.
   */
  setProcessSubjectActive(
    id: string,
    active: boolean,
  ): Promise<ProcessSubject> {
    if (useDemoApi) return demoApi.setProcessSubjectActive(id, active);
    return request<ProcessSubject>(
      `/process-subjects/${encodeURIComponent(id)}/active`,
      { method: "POST", body: JSON.stringify({ active }) },
    );
  },
  /** TUT-172 — renomear o assunto do escritório, e o passado junto. */
  renameProcessSubject(
    id: string,
    input: CreateProcessSubjectInput,
  ): Promise<ProcessSubjectRenameResponse> {
    if (useDemoApi) return demoApi.renameProcessSubject(id, input);
    return request<ProcessSubjectRenameResponse>(
      `/process-subjects/${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(input) },
    );
  },
  process(id: string): Promise<ProcessDetail> {
    if (useDemoApi) return demoApi.process(id);
    // TUT-472 (revisão de Q044) — a API valida o id com
    // `ParseUUIDPipe({ version: "4" })` e responde 400 em inglês a um endereço
    // como `/processos/nao-existe`; a ficha mostraria "Tentar novamente" sem
    // caminho de volta. Nenhum processo tem esse id, então a resposta honesta
    // é a do registro inexistente, sem ir à rede.
    if (!UUID_V4.test(id)) {
      return Promise.reject(new ApiError("Processo não encontrado.", 404));
    }
    return request<ProcessDetail>(`/processes/${encodeURIComponent(id)}`);
  },
  /**
   * TUT-194 — a confirmação humana da etapa dispensada por regra do cliente.
   * O servidor recusa (409) fora de PENDING; a tela só precisa mostrar o
   * erro, que já chega pelo toast padrão.
   */
  decideQuestionsWaiver(
    processId: string,
    decision: "CONFIRMED_ABSENT" | "REACTIVATED",
  ): Promise<{
    status: StageWaiverStatus;
    decidedByName: string | null;
    decidedAt: string | null;
  }> {
    if (useDemoApi)
      return demoApi.decideQuestionsWaiver(processId, decision);
    return request<{
      status: StageWaiverStatus;
      decidedByName: string | null;
      decidedAt: string | null;
    }>(`/processes/${encodeURIComponent(processId)}/questions-waiver`, {
      method: "POST",
      body: JSON.stringify({ decision }),
    });
  },
  processActivity(processId: string): Promise<ProcessActivityResponse> {
    return useDemoApi
      ? demoApi.processActivity(processId)
      : request<ProcessActivityResponse>(
          `/processes/${encodeURIComponent(processId)}/activity`,
        );
  },
  provenance(processId: string): Promise<ProcessProvenance> {
    return useDemoApi
      ? demoApi.provenance(processId)
      : request<ProcessProvenance>(
          `/processes/${encodeURIComponent(processId)}/provenance`,
        );
  },
  createProcess(input: CreateProcessInput): Promise<ProcessSummary> {
    return useDemoApi
      ? demoApi.createProcess(input)
      : request<ProcessSummary>("/processes", {
          method: "POST",
          body: JSON.stringify(input),
        });
  },
  /** TUT-567 — o catálogo de subtipos (Insalubridade e Periculosidade) que a árvore do objeto desenha. */
  processSubtipos(): Promise<{ items: AgenteDoKpi[] }> {
    return useDemoApi
      ? demoApi.processSubtipos()
      : request<{ items: AgenteDoKpi[] }>("/processes/subtipos");
  },
  /** TUT-567 — o que o processo tem (impede excluir) e o que está em aberto (o aviso de inativar). */
  processVinculos(processId: string): Promise<VinculosDoProcesso> {
    return useDemoApi
      ? demoApi.processVinculos(processId)
      : request<VinculosDoProcesso>(`/processes/${encodeURIComponent(processId)}/vinculos`);
  },
  /** TUT-567 — "Inativo" é `archived`: `false` inativa, `true` reativa. Devolve o detalhe como ficou. */
  setProcessActive(processId: string, active: boolean): Promise<ProcessDetail> {
    return useDemoApi
      ? demoApi.setProcessActive(processId, active)
      : request<ProcessDetail>(`/processes/${encodeURIComponent(processId)}/active`, {
          method: "POST",
          body: JSON.stringify({ active }),
        });
  },
  /** TUT-567 — só o processo sem perícia, peça, laudo nem envio; o resto é 409 `HAS_LINKS`. */
  deleteProcess(processId: string): Promise<void> {
    return useDemoApi
      ? demoApi.deleteProcess(processId)
      : request<void>(`/processes/${encodeURIComponent(processId)}`, { method: "DELETE" });
  },
  lookupDatajud(number: string): Promise<DatajudLookupResult> {
    if (useDemoApi) return demoApi.lookupDatajud(number);
    return request<DatajudLookupResult>(
      `/processes/datajud/${encodeURIComponent(number)}`,
    );
  },
  updateProcess(
    processId: string,
    input: UpdateProcessInput,
  ): Promise<ProcessDetail> {
    return useDemoApi
      ? demoApi.updateProcess(processId, input)
      : request<ProcessDetail>(`/processes/${encodeURIComponent(processId)}`, {
          method: "PATCH",
          body: JSON.stringify(input),
        });
  },
};
